/**
 * Buy More, Save More — dynamic cart progress message.
 *
 * Purely a UI layer. Shopify's automatic discounts (Admin > Discounts) remain
 * the source of truth for what is actually applied at checkout; this script
 * only reads cart quantity and updates a message + progress bar to encourage
 * reaching the next tier. It does not add, remove, or calculate discounts.
 *
 * Hooks into the theme's existing `cart:updated` event (dispatched by
 * theme.js after every AJAX cart change: add, remove, quantity change),
 * so no changes to core cart/add-to-cart/checkout logic are required.
 *
 * The very first "Add to cart" from a product page fires `ajaxProduct:added`
 * slightly ahead of the theme's own `cart:updated` refresh, which can race
 * and leave this UI reading a stale (pre-add) cart snapshot. To guarantee
 * correctness regardless of that internal timing, this script also does its
 * own lightweight `/cart.js` fetch right when `ajaxProduct:added` fires.
 *
 * The quantity +/- buttons and typed quantity changes in the cart are still
 * genuinely AJAX (they always were), but there's a real network round-trip
 * before the theme confirms the change and fires `cart:updated` — on a slow
 * connection that can feel like a 1-2s lag, which is especially annoying if
 * someone is clicking "+" repeatedly. To remove that felt delay, this script
 * also updates the message optimistically the instant a qty button is
 * clicked or a qty input is changed, using the value already visible in the
 * DOM at that moment. The real `cart:updated` event still runs afterwards as
 * the authoritative correction (e.g. if a quantity gets clamped by stock).
 */
(function () {
  if (window.__solqiaVolumeDiscountProgressInit) {
    return;
  }
  window.__solqiaVolumeDiscountProgressInit = true;

  function buildMessage(qty, tier2Qty, tier2Percent, tier3Qty, tier3Percent) {
    if (qty <= 0) {
      return { message: '', fillPercent: 0, visible: false };
    }

    if (qty < tier2Qty) {
      var remainingToTier2 = tier2Qty - qty;
      var itemWordTier2 = remainingToTier2 === 1 ? 'item' : 'items';
      return {
        message: 'Add ' + remainingToTier2 + ' more ' + itemWordTier2 + ' to unlock ' + tier2Percent + '% off',
        fillPercent: (qty / tier3Qty) * 100,
        visible: true
      };
    }

    if (qty < tier3Qty) {
      var remainingToTier3 = tier3Qty - qty;
      var itemWordTier3 = remainingToTier3 === 1 ? 'item' : 'items';
      return {
        message: tier2Percent + '% off unlocked • Add ' + remainingToTier3 + ' more ' + itemWordTier3 + ' to unlock ' + tier3Percent + '% off',
        fillPercent: (qty / tier3Qty) * 100,
        visible: true
      };
    }

    return {
      message: tier3Percent + '% off unlocked',
      fillPercent: 100,
      visible: true
    };
  }

  function getCartQuantity(cart) {
    if (!cart || !cart.items) {
      return 0;
    }

    return cart.items.reduce(function (total, item) {
      return total + (item.quantity || 0);
    }, 0);
  }

  function updateElement(el, qty) {
    var tier2Qty = parseInt(el.getAttribute('data-tier-2-qty'), 10) || 2;
    var tier2Percent = parseInt(el.getAttribute('data-tier-2-percent'), 10) || 15;
    var tier3Qty = parseInt(el.getAttribute('data-tier-3-qty'), 10) || 3;
    var tier3Percent = parseInt(el.getAttribute('data-tier-3-percent'), 10) || 20;

    var result = buildMessage(qty, tier2Qty, tier2Percent, tier3Qty, tier3Percent);

    var messageEl = el.querySelector('[data-volume-discount-progress-message]');
    var fillEl = el.querySelector('[data-volume-discount-progress-fill]');

    if (messageEl) {
      messageEl.textContent = result.message;
    }

    if (fillEl) {
      fillEl.style.width = result.fillPercent + '%';
    }

    el.classList.toggle('hide', !result.visible);
  }

  function updateAll(cart) {
    var qty = getCartQuantity(cart);
    var elements = document.querySelectorAll('[data-volume-discount-progress]');

    elements.forEach(function (el) {
      updateElement(el, qty);
    });
  }

  function getOptimisticQtyForForm(form) {
    var inputs = form.querySelectorAll('.js-qty__num');
    var total = 0;

    inputs.forEach(function (input) {
      var value = parseInt(input.value, 10);
      total += isNaN(value) ? 0 : value;
    });

    return total;
  }

  function updateOptimisticallyFromEvent(evt) {
    var form = evt.target.closest('form');
    if (!form) {
      return;
    }

    var progressEl = form.querySelector('[data-volume-discount-progress]');
    if (!progressEl) {
      return;
    }

    // Defer to after the click's own handler has updated the input's value.
    window.setTimeout(function () {
      updateElement(progressEl, getOptimisticQtyForForm(form));
    }, 0);
  }

  function refreshFromServer() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        updateAll(cart);
      })
      .catch(function () {});
  }

  document.addEventListener('cart:updated', function (evt) {
    if (evt && evt.detail && evt.detail.cart) {
      updateAll(evt.detail.cart);
    }
  });

  // Belt-and-braces for the very first add from a product page (see comment above).
  document.addEventListener('ajaxProduct:added', refreshFromServer);

  // Instant (optimistic) feedback for the qty +/- buttons and typed qty changes.
  document.addEventListener('click', function (evt) {
    if (evt.target.closest('.js-qty__adjust')) {
      updateOptimisticallyFromEvent(evt);
    }
  });

  document.addEventListener('change', function (evt) {
    if (evt.target.matches && evt.target.matches('.js-qty__num')) {
      updateOptimisticallyFromEvent(evt);
    }
  });
})();
